import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    IconButton,
    InputAdornment,
} from '@mui/material';
import { Clear } from '@mui/icons-material';
import { renameChatSession } from '../../../../api/sidebarAPI';

const RenameDialog = ({ open, initialTitle, thread_id, onConfirm, onCancel }) => {
    const [newTitle, setNewTitle] = React.useState(initialTitle);
    const [isRenaming, setIsRenaming] = React.useState(false);

    // Reset newTitle when dialog opens or initialTitle changes
    React.useEffect(() => {
        setNewTitle(initialTitle);
        setIsRenaming(false); // Reset loading state when dialog opens
    }, [initialTitle, open]);

    const handleClearInput = () => {
        setNewTitle('');
    };

    const handleConfirm = async () => {
        if (!newTitle.trim() || newTitle === initialTitle) {
            return; // Prevent submission if input is empty or unchanged
        }
        setIsRenaming(true);
        try {
            await renameChatSession({ thread_id, new_thread_name: newTitle.trim() });
            onConfirm(newTitle.trim());
        } catch (error) {
            console.error('Failed to rename chat session:', error);
            onConfirm(newTitle.trim()); // Update local state even if API fails
        } finally {
            setIsRenaming(false);
        }
        onCancel();
    };

    return (
        <Dialog
            open={open}
            onClose={onCancel}
            PaperProps={{
                sx: {
                    width: '320px',
                    maxWidth: '90%',
                    borderRadius: '12px',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
                    backgroundColor: '#ffffff',
                    padding: '12px',
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#1a1a1a',
                    padding: '12px 16px',
                    borderBottom: '1px solid #e0e0e0',
                }}
            >
                Rename Chat
            </DialogTitle>
            <DialogContent
                sx={{
                    padding: '16px',
                    '& .MuiTextField-root': {
                        marginTop: '8px',
                    },
                }}
            >
                <TextField
                    autoFocus
                    fullWidth
                    variant="outlined"
                    label="Chat Title"
                    value={newTitle}
                    onChange={(e) => setNewTitle(e.target.value)}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter' && !isRenaming && newTitle.trim() && newTitle !== initialTitle) {
                            handleConfirm();
                        }
                    }}
                    disabled={isRenaming}
                    InputProps={{
                        sx: {
                            fontSize: '14px',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#e0e0e0',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#b0b0b0',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#1976d2',
                                borderWidth: '2px',
                            },
                        },
                        endAdornment:
                            newTitle && !isRenaming ? (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleClearInput} size="small" sx={{ color: '#5d5d5d' }}>
                                        <Clear fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            ) : null,
                    }}
                    InputLabelProps={{
                        sx: {
                            fontSize: '14px',
                            color: '#5d5d5d',
                            '&.Mui-focused': {
                                color: '#1976d2',
                            },
                        },
                    }}
                />
            </DialogContent>
            <DialogActions
                sx={{
                    padding: '12px 16px',
                    borderTop: '1px solid #e0e0e0',
                    gap: '8px',
                }}
            >
                <Button
                    onClick={onCancel}
                    disabled={isRenaming}
                    sx={{
                        fontSize: '14px',
                        textTransform: 'none',
                        color: '#5d5d5d',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!newTitle.trim() || newTitle === initialTitle || isRenaming}
                    variant="contained"
                    sx={{
                        fontSize: '14px',
                        textTransform: 'none',
                        backgroundColor: '#44338A',
                        color: '#ffffff',
                        padding: '6px 16px',
                        borderRadius: '8px',
                        '&:hover': {
                            backgroundColor: '#1565c0',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: '#e0e0e0',
                            color: '#9e9e9e',
                        },
                    }}
                >
                    {isRenaming ? <CircularProgress size={20} color="inherit" /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RenameDialog;
